import type {SecondaryTimelineSource} from '@github-ui/timeline-items/SecondaryTimelineSource'
import {timelineItemKey} from '@github-ui/timeline-items/TimelineItemKey'
import {type ReactNode, useMemo} from 'react'

import type {RolledUpTimelineItem} from '../../utils/timeline-rollups'
import {parseGitHubIssueUrl} from '../../utils/urls'
import type {TimelineItem} from './IssueTimeline'
import type {IssueTimelineItemFragment} from './IssueTimelineItem'

/** Ids of every `ReferencedEvent` on the page, including the ones folded into a rollup group. */
export function referencedEventIds(timelineItems: TimelineItem[]): string[] {
  return timelineItems
    .filter(item => item.type === 'event' && item.item?.__typename === 'ReferencedEvent')
    .flatMap(timelineNode => {
      const node = timelineNode as RolledUpTimelineItem<IssueTimelineItemFragment>
      if (node.rollupGroup?.['ReferencedEvent']) {
        return node.rollupGroup['ReferencedEvent'].map(rollup => timelineItemKey(rollup))
      }

      return node.item ? [timelineItemKey(node.item)] : []
    })
}

type SecondaryTimelineSourceProps = {
  timelineItems: TimelineItem[]
  issueUrl: string
  children: (source?: SecondaryTimelineSource) => ReactNode
}

/** Reads the same data from the endpoint. Every badge shares one query key, so a single request
 * serves the page as it did under Relay. */
export function SecondaryTimelineSourceProvider({timelineItems, issueUrl, children}: SecondaryTimelineSourceProps) {
  const eventIds = useMemo(() => referencedEventIds(timelineItems), [timelineItems])
  const issue = useMemo(() => parseGitHubIssueUrl(issueUrl), [issueUrl])

  const source = useMemo(
    () => (eventIds.length > 0 && issue ? ({kind: 'endpoint', ...issue, eventIds} as const) : undefined),
    [eventIds, issue],
  )

  return <>{children(source)}</>
}
